import React from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
} from '../styles';
import { Container, Heading } from './ui';

const StyledIconSteps = styled.section`
  text-align: center;

  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        ${sectionMargins()};
      `;
    }
  }}
`;

const StyledContainer = styled(Container)`
  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        padding-right: 0;
        padding-left: 0;
      `;
    }
  }}
`;

const StyledInner = styled.div`
  background-color: ${brandColours.tertiary};
  border-radius: 12px;

  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        padding: 50px 30px;

        ${minBreakpointQuery.mlarge`
          padding-top: 60px;
          padding-bottom: 60px;
        `}

        ${minBreakpointQuery.large`
          padding-top: 80px;
          padding-bottom: 80px;
        `}

        ${minBreakpointQuery.xlarge`
          padding-top: 100px;
          padding-bottom: 100px;
        `}
      `;
    } else {
      return css`
        padding: 30px 10px;
      `;
    }
  }}
`;

const StyledHeader = styled.header`
  margin-bottom: 30px;

  ${minBreakpointQuery.medium`
    margin-bottom: 45px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-bottom: 55px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 65px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-bottom: 75px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 85px;
  `}

  ${minBreakpointQuery.xxxlarge`
    margin-bottom: 95px;
  `}
`;

const StyledHeading = styled(Heading)`
  ${fluidFontSize(
    '25px',
    '45px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
`;

const StyledText = styled.p`
  margin-top: 15px;

  ${minBreakpointQuery.small`
    ${fontSize(18)}
  `}

  ${minBreakpointQuery.large`
    ${fontSize(20)}
  `}
`;

const StyledItems = styled.div`
  display: flex;
  justify-content: center;

  ${maxBreakpointQuery.medium`
    flex-direction: column;
  `}

  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        ${maxBreakpointQuery.medium`
          row-gap: 60px;
        `}

        ${minBreakpointQuery.medium`
          column-gap: 60px;
        `}
  
        ${minBreakpointQuery.mlarge`
          column-gap: 75px;
        `}

        ${minBreakpointQuery.large`
          column-gap: 90px;
        `}

        ${minBreakpointQuery.xlarge`
          column-gap: 115px;
        `}

        ${minBreakpointQuery.xxlarge`
          column-gap: 130px;
        `}

        ${minBreakpointQuery.xxxlarge`
          column-gap: 140px;
        `}
      `;
    } else {
      return css`
        ${maxBreakpointQuery.medium`
          row-gap: 40px;
        `}

        ${minBreakpointQuery.medium`
          column-gap: 40px;
        `}
      `;
    }
  }}
`;

const StyledItem = styled.article`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: -40px;
    height: 20px;
    width: 1px;
    transform: scaleY(2);

    ${maxBreakpointQuery.medium`
      right: 0;
      border-right: 1px dashed ${standardColours.lightGrey};
      margin: 0 auto;
    `}

    ${minBreakpointQuery.medium`
      top: 36px;
      left: -15px;
      height: 1px;
      width: 30px;
      transform: scaleX(2) translateX(-50%);
      border-bottom: 1px dashed ${standardColours.lightGrey};
    `}

    ${minBreakpointQuery.mlarge`
      width: 35px;
      left: -20px;
    `}

    ${minBreakpointQuery.large`
      width: 40px;
      left: -25px;
    `}

    ${minBreakpointQuery.xlarge`
      width: 50px;
      left: -30px;
    `}

    ${minBreakpointQuery.xxlarge`
      width: 55px;
      left: -35px;
    `}

    ${minBreakpointQuery.xxxlarge`
      width: 60px;
      left: -40px;
    `}
  }

  &:first-child {
    &:before {
      display: none;
    }
  }
`;

const StyledIcon = styled.img`
  width: 72px;
  height: 72px;
`;

const StyledItemHeading = styled.h3`
  margin-top: 12px;
  ${fontSize(19)};

  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        ${minBreakpointQuery.small`
          ${fontSize(22)};
        `}

        ${minBreakpointQuery.large`
          margin-top: 15px;
          ${fontSize(25)};
        `}

        ${minBreakpointQuery.xlarge`
          margin-top: 20px;
        `}

        ${minBreakpointQuery.xlarge`
          margin-top: 25px;
        `}
      `;
    }
  }}
`;

const StyledItemText = styled.p`
  margin-top: 10px;
  line-height: 1.5;

  ${({ isGraphicDesignPopup }) => {
    if (!isGraphicDesignPopup) {
      return css`
        ${minBreakpointQuery.large`
          ${fontSize(18)}
        `}
      `;
    }
  }}
`;

const IconSteps = ({ heading, text, items, isGraphicDesignPopup }) => (
  <StyledIconSteps isGraphicDesignPopup={isGraphicDesignPopup}>
    <StyledContainer wide={true}>
      <StyledInner isGraphicDesignPopup={isGraphicDesignPopup}>
        <Container>
          {(heading || text) && (
            <StyledHeader>
              {heading && <StyledHeading>{heading}</StyledHeading>}
              {text && <StyledText>{text}</StyledText>}
            </StyledHeader>
          )}
          <StyledItems isGraphicDesignPopup={isGraphicDesignPopup}>
            {items.map(({ icon, heading, text }, id) => (
              <StyledItem key={id}>
                <StyledIcon src={icon.url} alt={icon.alt} loading="lazy" />
                <StyledItemHeading isGraphicDesignPopup={isGraphicDesignPopup}>
                  {heading}
                </StyledItemHeading>
                {text && (
                  <StyledItemText isGraphicDesignPopup={isGraphicDesignPopup}>
                    {text}
                  </StyledItemText>
                )}
              </StyledItem>
            ))}
          </StyledItems>
        </Container>
      </StyledInner>
    </StyledContainer>
  </StyledIconSteps>
);

export default IconSteps;
